import { TriangleAlert } from "lucide-react"

import {
  Button,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { TooltipContentPlus } from "@f2e/ui/components/base-plus"

import {
  formatCredits,
  formatUsageTime,
  summarizeUsageMeta,
} from "@/lib/usageMeta"

interface UsageMetaTooltipProps {
  responseTimeMs?: number
  costCredits?: number
  showSummary?: boolean
  convId?: number
  copilotField?: string
  align?: "start" | "center" | "end"
  size?: "default" | "small"
}

/**
 * A reusable tooltip component that displays usage information
 * including response time and credit cost.
 */
export const UsageMetaTooltip = ({
  responseTimeMs,
  costCredits,
  showSummary = false,
  convId,
  copilotField = "usageMeta",
  align = "start",
  size = "default",
}: UsageMetaTooltipProps) => {
  const formattedTime = formatUsageTime(responseTimeMs)
  const formattedCost = formatCredits(costCredits)

  const summary = [
    formattedCost !== null ? `${formattedCost} Credits` : null,
    formattedTime !== null ? `${formattedTime} s` : null,
  ]
    .filter((value) => value !== null)
    .join(" · ")
  const buttonClassName = showSummary
    ? "border-0 h-6 w-auto px-1.5 text-xs font-normal text-muted-foreground"
    : size === "small"
      ? "border-0 h-6 w-6"
      : "border-0"

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="transition duration-300 ease-in-out dark:hover:bg-slate-800 hover:bg-slate-100 rounded-sm"
            data-copilot-field={copilotField}
            data-copilot-value={summarizeUsageMeta({
              responseTimeMs,
              costCredits,
              convId,
            })}
          >
            <Button
              size="icon"
              variant="outline"
              className={buttonClassName}
              aria-label={summary || undefined}
              style={{ backgroundColor: "transparent" }}
            >
              {showSummary ? (
                summary
              ) : (
                <TriangleAlert className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          </div>
        </TooltipTrigger>
        <TooltipContentPlus side="top" align={align}>
          <div className="text-xs">
            {formattedTime !== null && <div>Time: {formattedTime} s</div>}
            {formattedCost !== null && (
              <div>
                Cost:{" "}
                <span className="font-semibold">{formattedCost} Credits</span>
              </div>
            )}
          </div>
        </TooltipContentPlus>
      </Tooltip>
    </TooltipProvider>
  )
}

export default UsageMetaTooltip
